import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { useNavigate, useParams } from "react-router";
import { toast } from "sonner";
import { useAuth, JwtPayload } from "../contexts/AuthContext";
import { AuthorizationResult } from "../models/AuthorizationResult";
import { useOrganization } from "../contexts/OrganizationContext";
import { ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY } from "../constants/AuthStorageKeys";

let refreshInFlight: Promise<AuthorizationResult> | null = null;
// Module-level guard: multiple concurrent requests can fail refresh at the
// same time (e.g. a page with 5 queries loads with an expired token). Only
// the first failure should fire the toast + navigate; the rest should stay
// silent. Reset on a successful refresh so later sessions still notify.
let sessionExpiredNotified = false;

const refreshTokens = (apiUrl: string): Promise<AuthorizationResult> => {
  if (refreshInFlight) return refreshInFlight;

  const currentRefreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
  if (!currentRefreshToken) {
    return Promise.reject(new Error("No refresh token available"));
  }

  refreshInFlight = axios
    .create({ baseURL: apiUrl, timeout: 30000 })
    .post("api/refreshtoken", { refreshToken: currentRefreshToken })
    .then((response) => {
      const tokens = response.data as AuthorizationResult;
      localStorage.setItem(ACCESS_TOKEN_KEY, tokens.accessToken);
      localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
      sessionExpiredNotified = false;
      return tokens;
    })
    .finally(() => {
      refreshInFlight = null;
    });

  return refreshInFlight;
};

const isTokenExpired = (token: string): boolean => {
  try {
    const decoded = jwtDecode<JwtPayload>(token);
    if (!decoded.exp) return false;
    return decoded.exp < Math.floor(Date.now() / 1000);
  } catch {
    return true;
  }
};

/**
 * Resolves the token to send, refreshing it first if it has expired. Shared by both clients below so the refresh,
 * the session-expired toast and the logout redirect exist in exactly one place — a second copy would drift, and the
 * failure mode is a client that quietly stops authenticating after the access token's lifetime.
 */
const useAccessToken = () => {
  const { accessToken, login, logout } = useAuth();
  const navigate = useNavigate();
  const apiUrl = import.meta.env.VITE_API_URL ?? "/";

  return async (): Promise<string | null> => {
    const tokenToUse = localStorage.getItem(ACCESS_TOKEN_KEY) ?? accessToken;
    if (!tokenToUse || !isTokenExpired(tokenToUse)) return tokenToUse;

    try {
      const tokens = await refreshTokens(apiUrl);
      login(tokens.accessToken, tokens.refreshToken);
      return tokens.accessToken;
    } catch (error) {
      console.error("Failed to refresh access token:", error);
      if (!sessionExpiredNotified) {
        sessionExpiredNotified = true;
        toast.error("Session expired — please sign in again");
        logout();
        navigate("/login");
      }
      throw error;
    }
  };
};

const useApi = () => {
  const { accessToken } = useAuth();
  const resolveAccessToken = useAccessToken();
  const { organizationId } = useParams<{
    organizationId?: string;
  }>();

  const apiUrl = import.meta.env.VITE_API_URL ?? "/";
  const instance = axios.create({
    baseURL: apiUrl,
    timeout: 30000,
    headers: { Authorization: `Bearer ${accessToken}` },
  });

  instance.interceptors.request.use(async (config) => {
    const tokenToUse = await resolveAccessToken();

    if (tokenToUse) {
      config.headers["Authorization"] = `Bearer ${tokenToUse}`;
    }

    if (organizationId) {
      config.headers["Organization-ID"] = organizationId;
    }

    return config;
  });

  return instance;
};

/**
 * Sends a request with `fetch` and hands back the raw `Response`, so the caller can read the body while the server is
 * still writing it, or as a file. Same base URL, token refresh and organization header as {@link useApi}.
 *
 * axios cannot serve either: in the browser it buffers the entire response before resolving the promise, so progress
 * written mid-request would only arrive once the request had finished, and its 30s `timeout` aborts long before an
 * operation measured in minutes completes — or a CSV the server has to compute before its first byte. `fetch` imposes
 * no timeout of its own: an aborted request has to come from an explicit signal or a genuine network failure.
 */
export const useFetchApi = () => {
  const resolveAccessToken = useAccessToken();
  const { organizationId } = useParams<{ organizationId?: string }>();
  const apiUrl = import.meta.env.VITE_API_URL ?? "/";

  return async (
    path: string,
    { method, body, signal }: { method: "GET" | "POST"; body?: unknown; signal?: AbortSignal }
  ): Promise<Response> => {
    const tokenToUse = await resolveAccessToken();

    const headers: Record<string, string> = {};
    if (body !== undefined) headers["Content-Type"] = "application/json";
    if (tokenToUse) headers["Authorization"] = `Bearer ${tokenToUse}`;
    if (organizationId) headers["Organization-ID"] = organizationId;

    return fetch(`${apiUrl.replace(/\/$/, "")}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
    });
  };
};

/** POSTs through {@link useFetchApi}, for an operation that streams its progress. */
export const useStreamingApi = () => {
  const fetchApi = useFetchApi();
  return (path: string, body: unknown, signal?: AbortSignal): Promise<Response> =>
    fetchApi(path, { method: "POST", body, signal });
};

export default useApi;
